import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardBody,
  CardTitle,
  CardText,
  Container,
  Row,
  Col,
  Badge,
  Spinner,
  Button,
} from "reactstrap";
import { provincesRepo } from "data/repositories/provincesRepo";
import { dealsRepo } from "data/repositories/dealsRepo";
import { businessesRepo } from "data/repositories/businessesRepo";
import { placesClient } from "data/client/placesClient";
import ThemeBadge from "components/ThemeBadge";
import EmptyState from "components/EmptyState";
import { adminPaths } from "admin/paths";
import { seasonLabel } from "labels/tr";

function PlaceMedia({ imageUrl, title }) {
  if (!imageUrl) return null;
  return (
    <div
      style={{
        height: 160,
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        borderTopLeftRadius: "0.375rem",
        borderTopRightRadius: "0.375rem",
      }}
      role="img"
      aria-label={title || "Görsel"}
    />
  );
}

export default function ProvinceDetail() {
  const { id } = useParams();
  const [province, setProvince] = useState(null);
  const [deals, setDeals] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [googleHotels, setGoogleHotels] = useState([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [hotelsError, setHotelsError] = useState("");

  useEffect(() => {
    const prov = provincesRepo.getById(id);
    setProvince(prov);
    setDeals(dealsRepo.list().filter((d) => d.provinceId === id));
    setBusinesses(businessesRepo.listByProvince(id));

    if (prov) {
      setHotelsLoading(true);
      placesClient
        .searchHotels(prov.name, 6)
        .then(setGoogleHotels)
        .catch((err) => setHotelsError(err.message))
        .finally(() => setHotelsLoading(false));
    }
  }, [id]);

  if (!province) {
    return (
      <>
        <div className="header bg-gradient-info pb-6 pt-5 pt-md-7" />
        <Container className="mt--6" fluid>
          <EmptyState title="Şehir bulunamadı" />
        </Container>
      </>
    );
  }

  const heroImage = province.heroImage || province.historyCoverImage;

  return (
    <>
      <div
        className="header pb-6 pt-5 pt-md-7"
        style={
          heroImage
            ? {
                backgroundImage: `linear-gradient(rgba(17, 24, 39, 0.55), rgba(17, 24, 39, 0.7)), url(${heroImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : {
                background: "linear-gradient(87deg, #11cdef 0, #1171ef 100%)",
              }
        }
      >
        <Container fluid>
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h1 className="text-white mb-1">{province.name}</h1>
              <p className="text-white opacity-8 mb-1">{province.slogan}</p>
              {province.landmark ? (
                <Badge color="light" className="text-dark">
                  {province.landmark}
                </Badge>
              ) : null}
            </div>
            <Button
              tag={Link}
              to={`/admin${adminPaths.content.provinces.edit(province.id)}`}
              color="white"
              size="sm"
              className="mt-2"
            >
              İçeriği düzenle
            </Button>
          </div>
        </Container>
      </div>

      <Container className="mt--6" fluid>
        <Card className="shadow mb-4">
          <CardBody>
            <h4>Tanıtım</h4>
            <p className="text-muted">{province.story}</p>

            {province.foods?.length ? (
              <>
                <h5>Yerel lezzetler</h5>
                <div className="mb-3">
                  {province.foods.map((food) => (
                    <Badge key={food} color="info" pill className="mr-1 mb-1">
                      {food}
                    </Badge>
                  ))}
                </div>
              </>
            ) : null}

            <h5>Temalar</h5>
            <div className="mb-3">
              {(province.themes || []).map((t) => (
                <ThemeBadge key={t} theme={t} />
              ))}
            </div>

            <h5>Mevsimler</h5>
            <div>
              {(province.seasons || []).map((s) => (
                <Badge key={s} color="light" className="mr-1 text-dark">
                  {seasonLabel(s)}
                </Badge>
              ))}
            </div>
          </CardBody>
        </Card>

        {(province.history || province.historyCoverImage) && (
          <Card className="shadow mb-4 overflow-hidden">
            {province.historyCoverImage ? (
              <PlaceMedia imageUrl={province.historyCoverImage} title={`${province.name} tarihçe`} />
            ) : null}
            <CardBody>
              <h4>Tarihçe</h4>
              <p className="text-muted mb-0">{province.history || "Tarihçe metni henüz eklenmedi."}</p>
            </CardBody>
          </Card>
        )}

        {province.highlights?.length > 0 && (
          <>
            <h4 className="mb-3">Ziyaret edilecek yerler</h4>
            <Row className="mb-4">
              {province.highlights.map((h) => (
                <Col key={h.id} lg={4} md={6} xs={12} className="mb-3">
                  <Card className="shadow h-100 overflow-hidden">
                    <PlaceMedia imageUrl={h.imageUrl} title={h.title} />
                    <CardBody>
                      <CardTitle tag="h5">{h.title}</CardTitle>
                      <CardText className="text-muted small">{h.shortDescription}</CardText>
                      <ThemeBadge theme={h.theme} />
                      {h.locationHint ? (
                        <small className="text-muted d-block mt-1">
                          <i className="fas fa-map-marker-alt mr-1" />
                          {h.locationHint}
                        </small>
                      ) : null}
                      {h.bestSeason ? (
                        <small className="text-muted d-block mt-1">
                          En iyi sezon: {seasonLabel(h.bestSeason) || h.bestSeason}
                        </small>
                      ) : null}
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        {province.experiences?.length > 0 && (
          <>
            <h4 className="mb-3">Deneyimler</h4>
            <Row className="mb-4">
              {province.experiences.map((e) => (
                <Col key={e.id} lg={4} md={6} xs={12} className="mb-3">
                  <Card className="shadow h-100 overflow-hidden">
                    <PlaceMedia imageUrl={e.imageUrl} title={e.title} />
                    <CardBody>
                      <CardTitle tag="h5">{e.title}</CardTitle>
                      <CardText className="text-muted small">{e.shortDescription}</CardText>
                      <ThemeBadge theme={e.theme} />
                      {e.locationHint ? (
                        <small className="text-muted d-block mt-1">
                          <i className="fas fa-map-marker-alt mr-1" />
                          {e.locationHint}
                        </small>
                      ) : null}
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        <h4 className="mb-3">Fırsatlar</h4>
        {deals.length === 0 ? (
          <EmptyState title="Fırsat yok" description="Bu şehir için henüz fırsat eklenmemiş." />
        ) : (
          <Row className="mb-4">
            {deals.map((d) => (
              <Col key={d.id} lg={4} md={6} xs={12} className="mb-3">
                <Card className="shadow h-100" tag={Link} to={`/admin/deals/${d.id}`} style={{ textDecoration: "none" }}>
                  <CardBody>
                    <CardTitle tag="h5">{d.title}</CardTitle>
                    <CardText className="text-muted small">{d.description}</CardText>
                    <Badge color="success">{d.priceText}</Badge>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <h4 className="mb-3">
          <i className="fas fa-hotel mr-2 text-primary" />
          Oteller
          <small className="text-muted ml-2">(Google Yerler)</small>
        </h4>
        {hotelsLoading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="text-muted mt-2">Oteller yükleniyor...</p>
          </div>
        )}
        {hotelsError && (
          <Card className="shadow-sm mb-3">
            <CardBody>
              <p className="text-muted mb-0">
                <i className="fas fa-exclamation-triangle text-warning mr-2" />
                {hotelsError}
              </p>
            </CardBody>
          </Card>
        )}
        {!hotelsLoading && !hotelsError && googleHotels.length === 0 && (
          <EmptyState title="Otel bulunamadı" description="Google Yerler'de bu bölgede otel sonucu yok." />
        )}
        <Row className="mb-4">
          {googleHotels.map((h) => (
            <Col key={h.id} lg={4} md={6} xs={12} className="mb-3">
              <Card className="shadow h-100">
                <CardBody>
                  <CardTitle tag="h5">{h.displayName?.text || "İsimsiz"}</CardTitle>
                  <CardText className="text-muted small mb-2">{h.formattedAddress}</CardText>
                  <div className="d-flex align-items-center">
                    {h.rating && (
                      <Badge color="warning" className="mr-2">
                        <i className="fas fa-star mr-1" />
                        {h.rating}
                      </Badge>
                    )}
                    {h.userRatingCount && (
                      <small className="text-muted">({h.userRatingCount} değerlendirme)</small>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <h4 className="mb-3">İşletmeler</h4>
        {businesses.length === 0 ? (
          <EmptyState title="İşletme yok" description="Bu şehir için henüz işletme eklenmemiş." />
        ) : (
          <Row className="mb-4">
            {businesses.map((b) => (
              <Col key={b.id} lg={4} md={6} xs={12} className="mb-3">
                <Card
                  className="shadow h-100"
                  tag={Link}
                  to={`/admin/businesses/${b.id}`}
                  style={{ textDecoration: "none" }}
                >
                  <CardBody>
                    <CardTitle tag="h5">{b.name}</CardTitle>
                    <CardText className="text-muted small">{b.description}</CardText>
                    <Badge color="info">{b.category}</Badge>
                    {b.rating && (
                      <small className="ml-2 text-warning">
                        <i className="fas fa-star" /> {b.rating}
                      </small>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </>
  );
}
